import { FC, useEffect, useRef, useState } from 'react'
import { ReactComponent as FacebookIcon } from 'src/assets/svg/facebook.svg'
import { ReactComponent as InstagramIcon } from 'src/assets/svg/instagram.svg'
import { CountryFlag } from 'src/components/CountryFlag'
import classes from './adCard.module.scss'
import { ReactComponent as InactiveIcon } from './emojione_white-heavy-check-mark.svg'
import { AdDetailsModal } from '../AdDetailsModal'

export type AdStatus = 'active' | 'inactive'

export interface AdCardData {
  assets: { links: string[]; photos: string[]; texts: string[] }
  body?: string
  id: string
  impressions?: { lower_bound: string; upper_bound: string }
  libraryId: string
  platforms: string[]
  // Meta's API returns no creative media URL, only a permalink to the rendered
  // ad on Facebook (ad_snapshot_url). It is a link, not an image.
  snapshotUrl?: string
  startedRunning: string
  status: AdStatus
  // Targeted countries (type === 'countries', excluded ones dropped).
  targetGeography: { code?: string; name: string }[]
}

interface AdCardProps {
  data: AdCardData
  isPolitical?: boolean
  pageId: string
  pageLogo: string
  pageName: string
}

const PLATFORM_ICONS: Record<string, FC<React.SVGProps<SVGSVGElement>>> = {
  facebook: FacebookIcon,
  instagram: InstagramIcon,
}

const MAX_VISIBLE_GEO = 3

export const AdCard: FC<AdCardProps> = ({
  data,
  isPolitical,
  pageLogo,
  pageName,
}) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [showAllGeo, setShowAllGeo] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const visibleGeo = showAllGeo
    ? data.targetGeography
    : data.targetGeography.slice(0, MAX_VISIBLE_GEO)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleCopyLink = () => {
    navigator.clipboard.writeText(
      `https://www.facebook.com/ads/library/?id=${data.libraryId}`
    )
    setMenuOpen(false)
  }
  return (
    <div className={classes.card}>
      {/* Card header */}
      <div className={classes.cardHeader}>
        <div className={classes.statusBadge}>
          {data.status === 'active' ? (
            <svg
              fill="none"
              height="16"
              viewBox="0 0 16 16"
              width="16"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="8" cy="8" fill="rgba(21,136,28,0.15)" r="8" />
              <path
                d="M4.5 8l2.5 2.5 4.5-5"
                stroke="#15881C"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
              />
            </svg>
          ) : (
            <InactiveIcon height="16" width="16" />
          )}
          <span
            className={
              data.status === 'active'
                ? classes.statusActive
                : classes.statusInactive
            }
          >
            {data.status === 'active' ? 'Active' : 'Inactive'}
          </span>
        </div>
        <div className={classes.menuWrapper} ref={menuRef}>
          <button
            className={classes.menuBtn}
            onClick={() => setMenuOpen((v) => !v)}
            type="button"
          >
            <span className={classes.dot} />
            <span className={classes.dot} />
            <span className={classes.dot} />
          </button>
          {menuOpen && (
            <div className={classes.menuDropdown}>
              <button
                className={classes.menuItem}
                onClick={handleCopyLink}
                type="button"
              >
                <svg
                  fill="none"
                  height="16"
                  viewBox="0 0 24 24"
                  width="16"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
                    stroke="#25262D"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
                    stroke="#25262D"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                  />
                </svg>
                <div className={classes.menuItemText}>
                  <span className={classes.menuItemTitle}>Copy ad link</span>
                  <span className={classes.menuItemSub}>
                    Get an Ad Library URL for this ad
                  </span>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Card info */}
      <div className={classes.cardInfo}>
        <span className={classes.infoText}>Library ID: {data.libraryId}</span>
        <span className={classes.infoText}>
          Started running on {data.startedRunning}
        </span>
        <div className={classes.platformsRow}>
          <span className={classes.infoText}>Platforms</span>
          <div className={classes.platformIcons}>
            {data.platforms.map((p) => {
              const Icon = PLATFORM_ICONS[p]
              return Icon ? (
                <div className={classes.platformIcon} key={p}>
                  <Icon />
                </div>
              ) : null
            })}
          </div>
        </div>
        {data.targetGeography.length > 0 && (
          <div className={classes.geoRow}>
            <span className={classes.infoText}>Target geography :</span>
            <div className={classes.geoChips}>
              {visibleGeo.map((geo) => (
                <span className={classes.geoChip} key={geo.name}>
                  <CountryFlag
                    className={classes.geoFlag}
                    code={geo.code}
                    name={geo.name}
                  />
                  {geo.name}
                </span>
              ))}
              {data.targetGeography.length > MAX_VISIBLE_GEO && (
                <button
                  className={classes.geoChipMore}
                  onClick={() => setShowAllGeo((v) => !v)}
                  type="button"
                >
                  {showAllGeo
                    ? 'Show less'
                    : `+${data.targetGeography.length - MAX_VISIBLE_GEO}`}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* See ad details button */}
      <button
        className={classes.seeDetailsBtn}
        onClick={() => setDetailsOpen(true)}
        type="button"
      >
        See ad details
      </button>

      {detailsOpen && (
        <AdDetailsModal
          data={data}
          isPolitical={isPolitical}
          onClose={() => setDetailsOpen(false)}
          pageLogo={pageLogo}
          pageName={pageName}
          platforms={data.platforms}
        />
      )}

      <div className={classes.previewDivider} />

      {/* Ad preview */}
      <div className={classes.adPreview}>
        <div className={classes.previewHeader}>
          <img alt={pageName} className={classes.previewLogo} src={pageLogo} />
          <div className={classes.previewInfo}>
            <span className={classes.previewName}>{pageName}</span>
            <span className={classes.previewSponsored}>Sponsored</span>
          </div>
        </div>
        <p className={classes.previewBody}>{data.body || ''}</p>
        {data.snapshotUrl && (
          <a
            className={classes.creativePreview}
            href={data.snapshotUrl}
            rel="noreferrer"
            target="_blank"
          >
            <svg
              fill="none"
              height="24"
              viewBox="0 0 24 24"
              width="24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                height="18"
                rx="2"
                stroke="#86949D"
                strokeWidth="1.5"
                width="18"
                x="3"
                y="3"
              />
              <circle
                cx="8.5"
                cy="8.5"
                r="1.5"
                stroke="#86949D"
                strokeWidth="1.5"
              />
              <path
                d="M21 15l-5-5L5 21"
                stroke="#86949D"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
              />
            </svg>
            <span className={classes.creativePreviewText}>
              Creative preview on Facebook
              <svg
                fill="none"
                height="14"
                viewBox="0 0 24 24"
                width="14"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 17L17 7M17 7H8M17 7V16"
                  stroke="#8080FF"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                />
              </svg>
            </span>
          </a>
        )}
        {isPolitical && data.impressions ? (
          <div className={classes.impressionsBadge}>
            Impressions: {data.impressions.lower_bound} -{' '}
            {data.impressions.upper_bound}
          </div>
        ) : (
          <div className={classes.previewFooter}>
            <span className={classes.previewCtaText}>
              განახორციელე სტაბილური ინვესტიცია 👉
            </span>
            <button className={classes.previewCtaBtn} type="button">
              Apply Now
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
